import microuniversoApi from "app/api/microuniversoApi";
import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { useAppSelector } from "app/redux/hooks";
import { FiltroPesquisaClientes } from "app/types/cliente/FiltroPesquisaClientes";
import ValidarInscricaoSuframa, { ICliente } from "app/types/ICliente";
import { IColuna } from "app/types/IColuna";
import { IEstado } from "app/types/IEstado";
import { IVendedor } from "app/types/IVendedor";
import { Parametro } from "app/types/Parametro";
import { Vendedor } from "app/types/Vendedor";
import { tratamentoErroPadrao } from "app/use-cases";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { atualizarClienteOrcamento } from "app/use-cases/orcamento/cliente/atualizarClienteOrcamento";
import { obterParametros } from "app/use-cases/parametro/obterParametros";
import { Dict } from "app/utils/Dict";
import globalMU from "globalMU";
import filter from "lodash/filter";
import indexOf from "lodash/indexOf";
import sortBy from "lodash/sortBy";
import React, { useEffect, useRef, useState } from "react";
import { substituirItemEmArray } from "substituirItemEmArray";
import { v4 as uuidv4 } from 'uuid';
import { DomainDataPendenciaCliente } from "../../types/cliente/DomainDataPendenciaCliente";
import { Breadcrumb2Container } from "../breadcrumb2/Breadcrumb2Container";
import { renderizarComponentReact } from "../renderizarOuRemoverComponentReact";
import { AbasPesquisaClienteContainer } from "./AbasPesquisaClienteContainer";
import {
  AbasPesquisaClienteNaoCadastradoContainer
} from "./dados-basicos-nao-cadastrado/AbasPesquisaClienteNaoCadastradoContainer";
import { PendenciaClienteContainer } from "./pendencia-cliente/PendenciaClienteContainer";
import { PesquisarCliente } from "./PesquisarCliente";
import { obterAlturaCabecalhoFixo } from "app/utils/alturaCabecalhoFixo";

declare var window: any;

const obterFiltrosIniciais = (): FiltroPesquisaClientes => ({
  Apelido: "",
  Bairro: "",
  Cidade: "",
  CpfCnpj: null,
  InscricaoEstadual: "",
  Nome: "",
  Telefone: "",
  OidVendedorRelacionado: "",
  NumeroPagina: 1,
  TamanhoPagina: 20,
  Uf: null,
});

const possuiResultadoPesquisaCliente = (clientes?: Partial<PagedMessageResponse<ICliente>>) =>
  (clientes?.PageCount ?? 0) > 0;

const obterClientesDaPesquisa = (clientes?: Partial<PagedMessageResponse<ICliente>>) =>
  Array.isArray(clientes?.Entities) ? clientes.Entities : [];

const normalizarPesquisaClientes = (clientes?: Partial<PagedMessageResponse<ICliente>>) => ({
  ...clientes,
  Entities: obterClientesDaPesquisa(clientes),
});

const obterColunasGrid = (colunas?: IColuna[]) =>
  Array.isArray(colunas) ? colunas : [];

// 493427 — o cabecalho do app e `position: fixed` (`.main-header`): alinhar a tabela no topo da
// janela esconderia as primeiras linhas atras dele. A altura vem MEDIDA de
// `obterAlturaCabecalhoFixo` — aqui havia um 120 chutado, e a altura real varia com a largura da
// janela. Esta folga e so para a primeira linha nao ficar colada no cabecalho.
const FOLGA_ABAIXO_DO_CABECALHO = 8;

// Ao clicar em Pesquisar, mostrar as LINHAS da tabela. Antes aqui era
// `window.scrollTo(0, document.body.scrollHeight)`, que jogava a pagina para o fim — abaixo da
// tabela, na legenda/rodape — e o usuario tinha de rolar de volta para ver o resultado.
const rolarAteTabelaDeResultados = () => {
  // A tabela e renderizada pelo React DEPOIS deste ponto (o setState dos clientes e assincrono),
  // entao espera ela existir em vez de rolar para uma posicao que ainda nao existe.
  const rolar = (tentativasRestantes: number) => {
    const tabela = document.getElementById("PesquisarCliente_TabelaContainer");

    if (!tabela) {
      if (tentativasRestantes > 0) requestAnimationFrame(() => rolar(tentativasRestantes - 1));
      return;
    }

    const posicaoNoDocumento = tabela.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({ top: Math.max(0, posicaoNoDocumento - obterAlturaCabecalhoFixo() - FOLGA_ABAIXO_DO_CABECALHO), behavior: "smooth" });
  };

  requestAnimationFrame(() => rolar(10));
};

export type PesquisarClienteContainerProps = {
  foraOrcamento?: boolean
  consultar?: boolean
  regiaoRenderizada: string
  clientes?: Partial<PagedMessageResponse<ICliente>>
  filtros?: FiltroPesquisaClientes
  colunas?: IColuna[]
  permiteEngrenagemGrid?: boolean
  tamanhoFonte?: number
  onVoltar?: () => void;
  passos?: Dict<string, () => void>;
  onRemover?: () => boolean
  onSelecionarClienteCallback?: (cliente: ICliente) => void
}

export const PesquisarClienteContainer = (props: PesquisarClienteContainerProps) => {
  const isMounted = useRef<boolean>(true)
  const colunasIniciais = obterColunasGrid(props.colunas)
  const atualizarGridExecutado = useRef<boolean>(colunasIniciais.length > 0)
  const clientesRef = useRef<Partial<PagedMessageResponse<ICliente>>>(normalizarPesquisaClientes(props.clientes))
  const filtrosRef = useRef<FiltroPesquisaClientes>(props.filtros ?? obterFiltrosIniciais())
  const colunasRef = useRef<IColuna[]>(colunasIniciais)
  const permiteEngrenagemGridRef = useRef<boolean>(props.permiteEngrenagemGrid ?? false)
  const tamanhoFonteRef = useRef<number>(props.tamanhoFonte ?? 0)
  const ultimosClientesComResultadoRef = useRef<ICliente[]>(obterClientesDaPesquisa(props.clientes))
  const nomeGrid = "PESQUISAR_CLIENTE_ORCAMENTO"
  const orcamento = useAppSelector((store) => store.orcamento);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [parametros, setParametros] = useState<Parametro[]>([]);
  const [clientes, setClientes] = useState<Partial<PagedMessageResponse<ICliente>>>(clientesRef.current);
  const [estados, setEstados] = useState<IEstado[]>([]);
  const [vendedor, setVendedor] = useState<IVendedor[]>([]);
  const [alterarVendedorRelacionado, setAlterarVendedorRelacionado] = useState<boolean>(false)
  const [vendedorLogado, setVendedorLogado] = useState<Vendedor>()
  const [colunas, setColunas] = useState<IColuna[]>(colunasRef.current);
  const [permiteEngrenagemGrid, setPermiteEngrenagemGrid] = useState<boolean>(permiteEngrenagemGridRef.current)
  const [tamanhoFonte, setTamanhoFonte] = useState<number>(tamanhoFonteRef.current);
  const [exibirBotaoClienteNaoCadastrado, setExibirBotaoClienteNaoCadastrado] = useState<boolean>(false);
  const [exibirBotaoClienteNovo, setExibirBotaoClienteNovo] = useState<boolean>(false);
  const [processandoPesquisarCliente, setProcessandoPesquisarCliente] = useState<boolean>(true);
  const [limparPesquisarCliente, setLimparPesquisarCliente] = useState(uuidv4())
  const [filtros, setFiltros] = useState<FiltroPesquisaClientes>(props.filtros ?? obterFiltrosIniciais());

  clientesRef.current = normalizarPesquisaClientes(clientes);
  filtrosRef.current = filtros;
  colunasRef.current = colunas;
  permiteEngrenagemGridRef.current = permiteEngrenagemGrid;
  tamanhoFonteRef.current = tamanhoFonte;
  if (clientesRef.current?.Entities?.length > 0) {
    ultimosClientesComResultadoRef.current = clientesRef.current.Entities;
  }

  const atualizarClientes = (clientesPesquisados?: Partial<PagedMessageResponse<ICliente>>) => {
    const clientesNormalizados = normalizarPesquisaClientes(clientesPesquisados);

    if (clientesNormalizados?.Entities?.length > 0) {
      ultimosClientesComResultadoRef.current = clientesNormalizados.Entities;
    }

    clientesRef.current = clientesNormalizados;
    setClientes(clientesNormalizados);
  };

  const obterClientesParaRetorno = () => {
    const clientesAtuais = normalizarPesquisaClientes(clientesRef.current);

    if (clientesAtuais?.Entities?.length === 0 && possuiResultadoPesquisaCliente(clientesAtuais)) {
      return {
        ...clientesAtuais,
        Entities: ultimosClientesComResultadoRef.current,
      };
    }

    return clientesAtuais;
  };

  const atualizarParametros = async () => {
    const result = await obterParametros({
      nomes: [
        "VISUALIZA_VENDEDOR_RELACIONADO_CONSCLIENTE",
        "VISUALIZA_CLIENTES_VENDEDOR_RELACIONADO", 
        "NOVO_CLIENTE_ORCAMENTO"
      ],
    })
    if (!isMounted.current) return
    setParametros(result)
  };

  const valorParametro = (nome: string) => parametros?.find((p) => p.Nome == nome)?.Valor;

  const permitidoAcaoFuncionalidade = async (siglaFuncionalidade: string, acao: string) => await obterPermissao({ funcionalidade: siglaFuncionalidade, acao });

  const atualizarExibicaoBotaoClienteNaoCadastrado = async () => {
    const result = await permitidoAcaoFuncionalidade("ORCAMENTO_CLIENTE_NAO_CADASTRADO", "INCLUIR")
    if (!isMounted.current) return
    setExibirBotaoClienteNaoCadastrado(result)
  };

  const atualizarExibicaoBotaoClienteNovo = async () => {
    const result = await permitidoAcaoFuncionalidade("ORCAMENTO_CLIENTE", "INCLUIR")
    if (!isMounted.current) return
    setExibirBotaoClienteNovo(result)
  };

  const pesquisar = async (filtros: FiltroPesquisaClientes) => {
    if (!isMounted.current) return;
    
    try {
      globalMU.blockUI();
      setProcessandoPesquisarCliente(true);
      setFiltros(filtros);
      await atualizarGridUmaVez();
      
      const clientesPesquisados = await microuniversoApi.Cliente.pesquisar(filtros);
      if (!isMounted.current) return;
      
      atualizarClientes(clientesPesquisados);
      rolarAteTabelaDeResultados();
    } catch (e) {
      if (isMounted.current) tratamentoErroPadrao(e);
    } finally {
      if (isMounted.current) {
        setProcessandoPesquisarCliente(false);
        globalMU.unblockUI();
      }
    }
  };

  const pesquisaDados = async () => {
    if (!isMounted.current) return;
    
    try {
      globalMU.blockUI();
      if (!props.onSelecionarClienteCallback && !props.foraOrcamento && orcamento.CodigoCliente > 7) {
        renderizarAbasPesquisaClienteContainer();
        return;
      }
      
      orcamento.ClienteNaoCadastrado && renderizarAbasClienteNaoCadastrado();
      const resposta = await microuniversoApi.Cliente.obterDadosTelaCliente(orcamento.IdentificadorOrcamento);
      if (!isMounted.current) return; // Verificação após a chamada assíncrona
      setAlterarVendedorRelacionado(resposta.UsuarioPermitidoAlterarVendedorRelacionado);
      setVendedorLogado(resposta.VendedorLogado);
      setEstados(resposta.Estados);
    } catch (e) {
      if (isMounted.current) tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  };

  const pesquisaVendedoresPorNome = async (nomeCodigoVendedor: string) => {
    if (!isMounted.current) return;
    
    try {
      globalMU.blockUI();
      const vendedoresPesquisados = await microuniversoApi.Pessoa.listarVendedoresPorNome(nomeCodigoVendedor);
      if (!isMounted.current) return;
      
      setVendedor(vendedoresPesquisados);
    } catch (e) {
      if (isMounted.current) tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  };

  const atualizarGrid = async (bloquearTela = true) => {
    if (!isMounted.current) return;

    try {
      if (bloquearTela) globalMU.blockUI();
      const [permiteEngrenagem, resposta] = await Promise.all([
        obterPermissao({ funcionalidade: "ORCAMENTO_ACESSO_ENGRENAGEM_GRID", acao: null }),
        microuniversoApi.Seguranca.obterDadosGrid("Orcamento", nomeGrid),
      ])
      if (!isMounted.current) return;

      permiteEngrenagemGridRef.current = permiteEngrenagem
      setPermiteEngrenagemGrid(permiteEngrenagem)
      if (resposta != null) {
        const colunasAtualizadas = obterColunasGrid(resposta.Colunas)

        colunasRef.current = colunasAtualizadas
        tamanhoFonteRef.current = resposta.TamanhoFonte
        setColunas(colunasAtualizadas);
        setTamanhoFonte(resposta.TamanhoFonte);
      }
    } catch (e) {
      if (isMounted.current) tratamentoErroPadrao(e);
    } finally {
      if (isMounted.current) {
        if (bloquearTela) globalMU.unblockUI();
        setCarregando(false);
      }
    }
  };

  const atualizarGridUmaVez = async () => {
    if (atualizarGridExecutado.current) return;

    atualizarGridExecutado.current = true;
    await atualizarGrid(false);
  };

  const toggleColunaSelecionada = async (nomeColuna: string, selecionada: boolean) => {
    let coluna = { ...colunas.find((c) => c.Nome == nomeColuna) };
    coluna.Selecionada = selecionada;

    const colunas_ = substituirItemEmArray(
      colunas,
      (coluna) => coluna.Nome == nomeColuna,
      coluna
    );

    setColunas(colunas_);
    colunasRef.current = colunas_;

    await microuniversoApi.Seguranca.atualizarColunasGrid(colunas_, nomeGrid);
  };

  const toggleSelecionarTodasColunas = async (selecionar: boolean) => {
    const colunas_ = colunas.map((c) => ({ ...c, Selecionada: selecionar } as IColuna));
    setColunas(colunas_);
    colunasRef.current = colunas_;
    await microuniversoApi.Seguranca.atualizarGrid({
      CodigoUsuario: globalMU.userIdForGrid(),
      Modulo: "Orcamento",
      Token: globalMU.token(),
      NomeGrid: nomeGrid,
      Colunas: colunas_
    })
  };

  const alterarFonte = async (tamanho: number) => {
    globalMU.blockUI()
    try {
      await microuniversoApi.Usuario.alterarFonte(nomeGrid, tamanho)
      if (!isMounted.current) return
      tamanhoFonteRef.current = tamanho
      setTamanhoFonte(tamanho)
    } catch (e) {
      tratamentoErroPadrao(e)
    }
    finally {
      globalMU.unblockUI()
    }
  };

  const ordenarColunas = (colunas: IColuna[], exibirVendedorRelacionado: boolean) => {
    const ordenacao = [
      "Nome",
      "CPF/CNPJ",
      "Endereço",
      "Bairro",
      "Cidade",
      "UF",
      "Telefone",
      "Vend.Relacionado",
      "ClienteAssociado"
    ]

    const colunasOrdenadas = sortBy(colunas, (coluna) => indexOf(ordenacao, coluna.Nome))
    return !exibirVendedorRelacionado ? filter(colunasOrdenadas, (coluna) => coluna.Nome != "Vend.Relacionado") : colunasOrdenadas
  }

  const onClienteModificado = (filtroPesquisaClientes: FiltroPesquisaClientes) => {
    filtrosRef.current = filtroPesquisaClientes;
    setFiltros(filtroPesquisaClientes)
  }

  const onLimpar = async () => {
    const filtrosIniciais = obterFiltrosIniciais();

    setLimparPesquisarCliente(uuidv4())
    filtrosRef.current = filtrosIniciais;
    setFiltros(filtrosIniciais);
    clientesRef.current = {};
    ultimosClientesComResultadoRef.current = [];
    setClientes({})
  };

  const onSelecionarCliente = async (cliente: ICliente) => {
    if (!isMounted.current || processandoPesquisarCliente) return;
    
    setProcessandoPesquisarCliente(true);
    
    const validarAreaVenda = async () => {
      if (cliente?.Codigo > 7 && orcamento.IdentificadorOrcamento) {
        const clienteSemAreaVenda = await microuniversoApi.Orcamento.VerificarClienteSemAreaVenda(orcamento.IdentificadorOrcamento, cliente?.Codigo);
        if (isMounted.current && clienteSemAreaVenda.toUpperCase() === "TRUE") {
          globalMU.showErrorMessage("Cliente não está associado a nenhuma área de vendas!");
        }
      }
    };

    try {
      globalMU.blockUI();
      if (!props.foraOrcamento) {
        const resposta = await atualizarClienteOrcamento(cliente);
        if (!isMounted.current) return;

        if (
          (resposta as DomainDataPendenciaCliente)?.ExistemPedidosEmAbertoClienteException ||
          (resposta as DomainDataPendenciaCliente)?.ClienteSemAreaDeVendaException ||
          (resposta as DomainDataPendenciaCliente)?.VendedorRelacionadoClienteDiferenteVendedorLogadoException ||
          (resposta as DomainDataPendenciaCliente)?.ClienteComLimiteCreditoBloqueadoException
        ) {
          if (isMounted.current) {
            renderizarComponentReact(
              <PendenciaClienteContainer
                onVoltar={funcaoRetornaPagina}
                cliente={cliente}
                domain={resposta as DomainDataPendenciaCliente}
                regiaoRenderizada={props.regiaoRenderizada}
              />,
              props.regiaoRenderizada,
              true
            );
            validarAreaVenda();
          }
          return;
        }

        if (isMounted.current) {
          validarAreaVenda();
          renderizarComponentReact(
            <AbasPesquisaClienteContainer codigoClienteSelecionado={cliente.Codigo} regiaoRenderizada={props.regiaoRenderizada} />,
            props.regiaoRenderizada,
            true
          );
          cliente.SaldoCashbackCliente > 0 && globalMU.showInfoMessage(`Cliente possui cashback de ${globalMU.formatarValorNumerico(cliente.SaldoCashbackCliente, 2, true)}!.`);
        }
        return;
      }

      if (isMounted.current) {
        renderizarComponentReact(
          <AbasPesquisaClienteContainer foraOrcamento regiaoRenderizada={props.regiaoRenderizada} codigoClienteSelecionado={cliente.Codigo}
            passos={passosInterno.current} onVoltar={funcaoRetornaPagina} />,
          props.regiaoRenderizada,
          true
        );
      }
    } catch (err) {
      if (isMounted.current) tratamentoErroPadrao(err);
    } finally {
      if (isMounted.current) {
        globalMU.unblockUI();
        setProcessandoPesquisarCliente(false);
        if (cliente?.Codigo > 7 && cliente.InscricaoSuframa) {
          const erroSuframa = ValidarInscricaoSuframa(cliente.InscricaoSuframa);
          if (erroSuframa) {
            globalMU.showErrorMessage(erroSuframa);
          }
        }
      }
    }
  };

  const renderizarAbasPesquisaClienteContainer = () => {
    renderizarComponentReact(
      <AbasPesquisaClienteContainer regiaoRenderizada={props.regiaoRenderizada} />,
      props.regiaoRenderizada,
      true
    );
  }

  const renderizarAbasClienteNaoCadastrado = () => renderizarComponentReact(
    <AbasPesquisaClienteNaoCadastradoContainer regiaoRenderizada={props.regiaoRenderizada} />,
    props.regiaoRenderizada,
    true
  );

  const funcaoRetornaPagina = () => {
    
    if (props.foraOrcamento) {
      window.AsideDireita?.ocultarMenuCliente?.()
    } else {
      window.AsideDireita?.show?.("valores-orcamento")
    }

    renderizarComponentReact(<PesquisarClienteContainer
      {...props}
      clientes={obterClientesParaRetorno()}
      filtros={filtrosRef.current}
      colunas={colunasRef.current}
      permiteEngrenagemGrid={permiteEngrenagemGridRef.current}
      tamanhoFonte={tamanhoFonteRef.current} />,
      props.regiaoRenderizada,
      true
    )
  }

  const passosInterno = useRef<Dict<string, () => void>>(props.passos ? new Dict(Object.assign({}, props.passos.map)) : new Dict())


  useEffect(() => {
    props.foraOrcamento ? passosInterno.current.set("Cliente", funcaoRetornaPagina) : passosInterno.current.set("Pesquisar Cliente", funcaoRetornaPagina);

    const executarFuncoes = async () => {
      try {
        await Promise.all([
          pesquisaDados(),
          atualizarParametros(),
          atualizarExibicaoBotaoClienteNaoCadastrado(),
          atualizarExibicaoBotaoClienteNovo(),
          possuiResultadoPesquisaCliente(props.clientes) && colunasRef.current.length === 0 ? atualizarGridUmaVez() : Promise.resolve(),
        ]);
      } finally {
        if (isMounted.current) setCarregando(false);
      }
    };

    executarFuncoes();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(()=>{
    if(parametros != undefined && parametros !== null && parametros.length > 0)
    {
        setProcessandoPesquisarCliente(false)
    }
  }, [parametros])

  return (
<>
{carregando
 ? <><span>Carregando... Aguarde!</span></> 
 : <>
      <Breadcrumb2Container
        passos={passosInterno.current.keysToList()}
        id="PesquisarClienteContainer"
        exibirBotaoLimpar={true}
        exibirBotaoVoltar={passosInterno.current.keysToList().length > 1}
        onBotaoLimparClicado={onLimpar}
        onBotaoVoltarClicado={props.onVoltar}
        onPassoSelecionado={(passo) => passosInterno.current.keysToList().length > 1 && passosInterno.current.get(passo)()}
      />

      <PesquisarCliente
        foraOrcamento={props.foraOrcamento}
        alterarVendedorRelacionado={alterarVendedorRelacionado}
        processandoPesquisarCliente={processandoPesquisarCliente}
        vendedorLogado={vendedorLogado}
        key={limparPesquisarCliente}
        permiteEngrenagemGrid={permiteEngrenagemGrid}
        filtrosPesquisaCliente={filtros}
        dadosClienteTabela={clientes}
        listaEstados={estados}
        exibirVendedorRelacionado={valorParametro("VISUALIZA_VENDEDOR_RELACIONADO_CONSCLIENTE") == "S"}
        visualizaApenasClientesVendedorRelacionado={valorParametro("VISUALIZA_CLIENTES_VENDEDOR_RELACIONADO") == "S"}
        mostrarBotoes={!props.consultar || props.foraOrcamento}
        orcamento={orcamento.Identificador}
        somenteLeitura={!props.foraOrcamento && orcamento != undefined && orcamento.SomenteLeitura}
        mostrarBotaoNovo={!props.consultar && (valorParametro("NOVO_CLIENTE_ORCAMENTO") != "N" && exibirBotaoClienteNovo)}
        mostrarBotaoNaoCadastrado={exibirBotaoClienteNaoCadastrado}
        onPesquisarVendedorRelacionado={pesquisaVendedoresPorNome}
        listaVendedores={vendedor}
        onNovoCliente={() => {
          renderizarComponentReact(
            <AbasPesquisaClienteContainer regiaoRenderizada={props.regiaoRenderizada} foraOrcamento={props.foraOrcamento} passos={passosInterno.current} onVoltar={funcaoRetornaPagina} />,
            props.regiaoRenderizada,
            true
          );
        }}
        onClienteNaoCadastrado={renderizarAbasClienteNaoCadastrado}
        onPesquisar={pesquisar}
        onAlterarFonte={alterarFonte}
        onClienteModificado={onClienteModificado}
        tamanhoFontePadrao={tamanhoFonte}
        onSelecionarCliente={(cliente) => {
          if (props.onSelecionarClienteCallback)
          {
            props.onSelecionarClienteCallback(cliente)
          }
          else 
            onSelecionarCliente(cliente)
        }
          }
        onToggleColunaSelecionada={toggleColunaSelecionada}
        onToggleSelecionarTodasColunas={toggleSelecionarTodasColunas}
        colunas={ordenarColunas(colunas, valorParametro("VISUALIZA_VENDEDOR_RELACIONADO_CONSCLIENTE") == "S")}
      />
    </>
}
    </>
  );
};